/**
 * US 13.9 — "Summarise" above a task's comments (5 or more): status,
 * decisions, open questions and who is waiting on whom, each point citing
 * the comment it came from (click to jump there). It can be refreshed after
 * new comments, and posted as a comment only after the person confirms.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { LoaderCircle, RefreshCw, Sparkles, X } from "lucide-react";
import { Modal } from "../hubs/parts";
import { mochiApi, type Summary } from "./api";

export const SUMMARY_MIN_COMMENTS = 5;

function jump(commentId: string) {
  document.getElementById(`task-comment-${commentId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
}

export function ThreadSummary({ taskId, commentCount, onPosted }: { taskId: string; commentCount: number; onPosted: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.toLowerCase().startsWith("ar") ? "ar" : "en";
  const [state, setState] = useState<{ summary: Summary; body: string; at: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  if (commentCount < SUMMARY_MIN_COMMENTS) return null;
  const run = () => {
    setBusy(true);
    mochiApi.summarize(taskId, lang).then(
      (r) => setState({ summary: r.summary, body: r.comment_body, at: commentCount }),
      (err) => toast.error((err as Error).message),
    ).finally(() => setBusy(false));
  };
  const cite = (id: string, by: string) => (
    <button type="button" onClick={() => jump(id)} className="ms-1 text-[11px] text-primary hover:underline">
      ({by})
    </button>
  );
  if (!state)
    return (
      <button type="button" onClick={run} disabled={busy} className="mb-3 h-9 px-3.5 rounded-xl border border-border text-xs font-bold hover:bg-muted flex items-center gap-1.5 disabled:opacity-50">
        {busy ? <LoaderCircle className="size-3.5 animate-spin" /> : <Sparkles className="size-3.5 text-primary" />}
        {t("tasks.summary.button", { defaultValue: "Summarise" })}
      </button>
    );
  const s = state.summary;
  const section = (title: string, rows: { text: string; comment: string; by: string }[]) =>
    rows.length > 0 && (
      <div>
        <p className="text-[11px] font-black text-muted-foreground uppercase tracking-widest">{title}</p>
        <ul className="list-disc ps-4 space-y-0.5">
          {rows.map((r) => (
            <li key={`${r.comment}-${r.text}`}>
              {r.text}
              {cite(r.comment, r.by)}
            </li>
          ))}
        </ul>
      </div>
    );
  return (
    <section aria-label={t("tasks.summary.title", { defaultValue: "Summary" })} className="mb-4 rounded-2xl border border-primary/30 bg-primary/[0.04] p-4 text-sm space-y-2">
      <header className="flex items-center gap-2">
        <Sparkles className="size-4 text-primary" />
        <span className="font-bold flex-1">{t("tasks.summary.title", { defaultValue: "Summary" })}</span>
        {commentCount > state.at && <span className="text-[11px] text-amber-700 dark:text-amber-300">{t("tasks.summary.newComments", { defaultValue: "New comments since" })}</span>}
        <button type="button" onClick={run} disabled={busy} aria-label={t("tasks.summary.refresh", { defaultValue: "Refresh summary" })} className="w-9 h-9 rounded-full bg-foreground/[0.06] hover:bg-foreground/[0.1] text-muted-foreground flex items-center justify-center shrink-0">
          <RefreshCw className={busy ? "size-3.5 animate-spin" : "size-3.5"} />
        </button>
        <button type="button" onClick={() => setState(null)} aria-label={t("tasks.summary.close", { defaultValue: "Close summary" })} className="w-9 h-9 rounded-full bg-foreground/[0.06] hover:bg-foreground/[0.1] text-muted-foreground flex items-center justify-center shrink-0">
          <X className="size-3.5" />
        </button>
      </header>
      {s.status && <p>{s.status}</p>}
      {section(t("tasks.summary.decisions", { defaultValue: "Decisions" }), s.decisions)}
      {section(t("tasks.summary.questions", { defaultValue: "Open questions" }), s.open_questions)}
      {s.waiting.length > 0 && (
        <div>
          <p className="text-[11px] font-black text-muted-foreground uppercase tracking-widest">{t("tasks.summary.waiting", { defaultValue: "Waiting" })}</p>
          <ul className="list-disc ps-4 space-y-0.5">
            {s.waiting.map((w) => (
              <li key={`${w.comment}-${w.who}`}>
                {t("tasks.summary.waitingOn", { who: w.who, on: w.on, defaultValue: "{{who}} is waiting on {{on}}" })}
                {cite(w.comment, w.by)}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex justify-end">
        <button type="button" onClick={() => setConfirming(true)} className="h-8 px-3 rounded-xl bg-primary text-primary-foreground text-xs font-bold">
          {t("tasks.summary.post", { defaultValue: "Post as comment" })}
        </button>
      </div>
      {confirming && (
        <Modal onClose={() => setConfirming(false)} labelledBy="post-summary-title" className="max-w-md" z="z-[90]">
          <div className="p-6 space-y-3">
            <h2 id="post-summary-title" className="text-lg font-bold">
              {t("tasks.summary.postTitle", { defaultValue: "Post this summary as a comment?" })}
            </h2>
            <pre className="whitespace-pre-wrap text-xs bg-muted/40 rounded-xl p-3 max-h-60 overflow-y-auto font-sans">{state.body}</pre>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setConfirming(false)} className="h-10 px-4 rounded-xl text-sm font-semibold hover:bg-muted">
                {t("tasks.cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirming(false);
                  mochiApi.postSummary(taskId, state.body).then(
                    () => {
                      toast.success(t("tasks.summary.posted", { defaultValue: "Summary posted" }));
                      onPosted();
                    },
                    (err) => toast.error((err as Error).message),
                  );
                }}
                className="h-10 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-bold"
              >
                {t("tasks.summary.postConfirm", { defaultValue: "Post comment" })}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}
